import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate, fmtDateTime, STATUS_META } from "@/lib/entries";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import StatusBadge from "@/components/StatusBadge";
import CloseFollowupDialog from "@/components/CloseFollowupDialog";
import { ArrowLeft, Trash2, PhoneCall, MessageCircle, MessageSquare, Star, FlaskConical, CalendarClock, CheckCircle2, XCircle, RotateCcw, Plus, Clock } from "lucide-react";

export default function EntryDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [entry, setEntry] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/entries/${id}`); setEntry(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    try { await api.delete(`/entries/${id}`); toast.success("Entry deleted"); nav(-1); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!entry) return <div className="p-10 text-sm text-slate-500">Loading...</div>;
  const meta = STATUS_META[entry.status];

  return (
    <div className="p-6 lg:p-10 max-w-[1200px] mx-auto">
      <button type="button" onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800"
              data-testid="back-button">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center font-display font-semibold text-lg">
              {entry.patient_name?.[0]?.toUpperCase()}
            </div>
            <div>
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900" data-testid="entry-patient-name">
                {entry.patient_name}
              </h1>
              <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                <StatusBadge status={entry.status} />
                <span>Logged {fmtDate(entry.entry_date)} · by {entry.created_by_name}</span>
                {entry.is_complete && (
                  <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 border-slate-200">Completed</Badge>
                )}
              </div>
            </div>
          </div>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                    data-testid="delete-entry-button">
              <Trash2 size={16} />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
              <AlertDialogDescription>This removes the entry and all its history. Cannot be undone.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={remove} data-testid="confirm-delete-entry">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <Card className="p-6 border-slate-200 lg:col-span-1">
          <h2 className="font-display font-semibold text-slate-900">Entry details</h2>
          <div className="mt-4 space-y-4 text-sm">
            <Field label="Sheet" value={<span>{meta.sheet}</span>} />
            <Field label="Notes" value={<span className="text-slate-800 whitespace-pre-wrap">{entry.notes || "—"}</span>} />
            {(entry.scheduled_date || entry.followup_date) && (
              <Field label="Follow-up date" icon={<CalendarClock size={14} />}
                     value={<span className="font-medium">{fmtDate(entry.scheduled_date || entry.followup_date)}</span>} />
            )}
            {entry.done_date && (
              <Field label="Follow-up done" icon={<CheckCircle2 size={14} />}
                     value={<span className="font-medium">{fmtDate(entry.done_date)}{entry.done_method ? ` · ${entry.done_method === "whatsapp" ? "WhatsApp" : "Call"}` : ""}</span>} />
            )}
            {entry.next_date && (
              <Field label="Next follow-up" icon={<CalendarClock size={14} />}
                     value={<span className="font-medium">{fmtDate(entry.next_date)}</span>} />
            )}
            {entry.status === "treatment_completed" && !entry.is_complete && (
              <Field label="Next review nudge" icon={<Star size={14} />}
                     value={<span>#{entry.review_stage} of 3 · {fmtDate(entry.next_review_reminder)}</span>} />
            )}
            {entry.status === "lab_work" && entry.lab_received_date && (
              <Field label="Received on" icon={<FlaskConical size={14} />}
                     value={<span>{fmtDate(entry.lab_received_date)}</span>} />
            )}
            {entry.is_complete && (
              <Field label="Completed at" icon={<CheckCircle2 size={14} />}
                     value={<span>{fmtDateTime(entry.completed_at)}</span>} />
            )}
          </div>
        </Card>

        <div className="lg:col-span-2 space-y-6">
          {/* Workflow-specific action card */}
          {!entry.is_complete && entry.status === "next_appointment" && (
            <Card className="p-6 border-slate-200 text-sm text-slate-600">
              This entry is a booked next appointment and is marked complete. No further action needed.
            </Card>
          )}

          {(entry.status === "ongoing_treatment" || entry.status === "cancelled_no_show" || entry.status === "followup_pending_other") && (
            <FollowupWorkspace entry={entry} onDone={load} />
          )}

          {!entry.is_complete && entry.status === "treatment_completed" && (
            <ReviewActions entry={entry} onDone={load} />
          )}

          {!entry.is_complete && entry.status === "lab_work" && (
            <LabActions entry={entry} onDone={load} />
          )}

          {/* Follow-up chain history */}
          {(entry.status === "ongoing_treatment" || entry.status === "cancelled_no_show") && (
            <ChainHistoryCard entryId={entry.id} />
          )}

          {/* History / timeline */}
          <HistoryCard entry={entry} />
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, icon }) {
  return (
    <div>
      <div className="text-slate-500 flex items-center gap-1.5 mb-1">{icon}{label}</div>
      <div className="text-slate-900">{value}</div>
    </div>
  );
}

/* ----- chain history for follow-up entries ----- */
function ChainHistoryCard({ entryId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/entries/${entryId}/chain`);
        if (alive) setRows(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [entryId]);

  if (loading) {
    return <Card className="p-6 border-slate-200 text-sm text-slate-500" data-testid="chain-loading">Loading follow-up history...</Card>;
  }

  return (
    <Card className="p-6 border-slate-200" data-testid="chain-history-card">
      <h2 className="font-display font-semibold text-slate-900 flex items-center gap-2">
        <CalendarClock size={18} className="text-sky-600" /> Follow-up chain history
      </h2>
      <p className="text-sm text-slate-500 mt-1">
        Every scheduled follow-up for this patient, when it was done and what was set for the next one.
      </p>
      {rows.length === 0 ? (
        <div className="mt-6 text-sm text-slate-500">No history yet.</div>
      ) : (
        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-4 font-medium">#</th>
                <th className="py-2 pr-4 font-medium">Scheduled</th>
                <th className="py-2 pr-4 font-medium">Done</th>
                <th className="py-2 pr-4 font-medium">Next</th>
                <th className="py-2 pr-4 font-medium">Outcome</th>
                <th className="py-2 pr-4 font-medium">Note</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const isCurrent = r.id === entryId;
                return (
                  <tr key={r.id}
                      className={`border-b border-slate-100 last:border-b-0 ${isCurrent ? "bg-sky-50/60" : ""}`}
                      data-testid={`chain-row-${i}`}>
                    <td className="py-2 pr-4 text-slate-500">{i + 1}</td>
                    <td className="py-2 pr-4 text-slate-900 whitespace-nowrap">
                      {fmtDate(r.scheduled_date || r.followup_date)}
                    </td>
                    <td className="py-2 pr-4 whitespace-nowrap">
                      {r.done_date ? (
                        <span className="text-slate-800">
                          {fmtDate(r.done_date)}
                          {r.done_method && (
                            <span className="ml-1 text-slate-400 text-xs inline-flex items-center gap-1">
                              {r.done_method === "whatsapp" ? <MessageCircle size={11} /> : <PhoneCall size={11} />}
                            </span>
                          )}
                        </span>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="py-2 pr-4 text-slate-700 whitespace-nowrap">
                      {r.next_date ? fmtDate(r.next_date) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="py-2 pr-4">
                      {r.done_outcome === "appointment_booked"
                        ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50">Booked</Badge>
                        : r.done_outcome === "reschedule"
                          ? <Badge className="bg-sky-50 text-sky-700 border-sky-100 hover:bg-sky-50">Rescheduled</Badge>
                          : r.done_outcome === "no_further"
                            ? <Badge className="bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-100">No further</Badge>
                            : <Badge className="bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-100">Pending</Badge>}
                    </td>
                    <td className="py-2 pr-4 text-slate-600 max-w-xs truncate">{r.done_note || r.notes || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/* ----- follow-up workspace ----- */
function FollowupWorkspace({ entry, onDone }) {
  return (
    <div className="space-y-6">
      <FollowupInfoCard entry={entry} onDone={onDone} />
      <CommunicationHistoryCard entry={entry} onDone={onDone} />
      <PatientTimelineCard patientName={entry.patient_name} currentEntryId={entry.id} />
    </div>
  );
}

function FollowupInfoCard({ entry, onDone }) {
  const [closeOpen, setCloseOpen] = useState(false);
  const [reopening, setReopening] = useState(false);

  const reopen = async () => {
    setReopening(true);
    try { await api.post(`/entries/${entry.id}/reopen`); toast.success("Follow-up reopened"); onDone(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setReopening(false); }
  };

  return (
    <Card className="p-6 border-slate-200" data-testid="followup-info-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display font-semibold text-slate-900 flex items-center gap-2">
            <CalendarClock size={18} className="text-sky-600" /> Follow-Up Information
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Follow-ups stay open until you explicitly close them. Logging a call or message here is a communication activity only.
          </p>
        </div>
        {!entry.is_complete ? (
          <Button onClick={() => setCloseOpen(true)}
                  className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="close-followup-button">
            <XCircle size={16} className="mr-1.5" /> Close Follow-Up
          </Button>
        ) : (
          <Button variant="outline" onClick={reopen} disabled={reopening}
                  data-testid="reopen-followup-button">
            <RotateCcw size={16} className="mr-1.5" /> {reopening ? "Reopening..." : "Reopen"}
          </Button>
        )}
      </div>

      <div className="mt-5 grid sm:grid-cols-2 gap-4 text-sm">
        <Field label="Scheduled date" icon={<CalendarClock size={14} />}
               value={<span className="font-medium">{fmtDate(entry.scheduled_date || entry.followup_date)}</span>} />
        <Field label="Current status"
               value={entry.is_complete
                 ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50">Closed</Badge>
                 : <Badge className="bg-sky-50 text-sky-700 border-sky-100 hover:bg-sky-50">Open</Badge>} />
        {entry.completed_at && (
          <Field label="Completed on" icon={<CheckCircle2 size={14} />}
                 value={<span className="font-medium">{fmtDateTime(entry.completed_at)}</span>} />
        )}
        {entry.next_date && (
          <Field label="Next follow-up" icon={<CalendarClock size={14} />}
                 value={<span className="font-medium">{fmtDate(entry.next_date)}</span>} />
        )}
      </div>

      {entry.closure && (
        <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50/60 p-4"
             data-testid="closure-block">
          <div className="text-xs text-slate-500">Closed by {entry.closure.by || "—"} · {fmtDateTime(entry.closure.closed_at)}</div>
          <div className="mt-1 font-medium text-slate-900">{entry.closure.reason}</div>
          {entry.closure.note && <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{entry.closure.note}</div>}
        </div>
      )}

      <CloseFollowupDialog open={closeOpen} onOpenChange={setCloseOpen} entry={entry} onClosed={onDone} />
    </Card>
  );
}

function CommunicationHistoryCard({ entry, onDone }) {
  const [kind, setKind] = useState("call");
  const [channel, setChannel] = useState("whatsapp");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!note.trim()) { toast.error("Please add a note"); return; }
    setSaving(true);
    try {
      await api.post(`/entries/${entry.id}/communications`, {
        kind,
        channel: kind === "message" ? channel : null,
        note,
      });
      setNote("");
      toast.success("Communication logged");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const removeOne = async (cid) => {
    try { await api.delete(`/entries/${entry.id}/communications/${cid}`); toast.success("Removed"); onDone(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // Merge legacy followup_history (from old flow) with new communications, sorted desc.
  const items = [
    ...(entry.communications || []).map((c) => ({ ...c, _legacy: false })),
    ...(entry.followup_history || []).map((h, i) => ({
      id: `legacy-${i}`, _legacy: true,
      kind: h.method === "call" ? "call" : "message",
      channel: h.method === "call" ? null : h.method,
      note: h.note || "", at: h.at, by: h.by,
    })),
  ].sort((a, b) => new Date(b.at) - new Date(a.at));

  return (
    <Card className="p-6 border-slate-200" data-testid="communication-history-card">
      <h2 className="font-display font-semibold text-slate-900 flex items-center gap-2">
        <MessageSquare size={18} className="text-sky-600" /> Communication History
      </h2>
      <p className="text-sm text-slate-500 mt-1">Every call and message logged against this follow-up, newest first.</p>

      {!entry.is_complete && (
        <form onSubmit={submit} className="mt-5 rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger data-testid="comm-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="call"><span className="inline-flex items-center gap-2"><PhoneCall size={14} /> Call</span></SelectItem>
                  <SelectItem value="message"><span className="inline-flex items-center gap-2"><MessageCircle size={14} /> Message</span></SelectItem>
                </SelectContent>
              </Select>
            </div>
            {kind === "message" && (
              <div className="space-y-2">
                <Label>Channel</Label>
                <Select value={channel} onValueChange={setChannel}>
                  <SelectTrigger data-testid="comm-channel-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Result / notes</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                      placeholder="What did they say?" data-testid="comm-note-input" />
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={saving}
                    className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="comm-submit-button">
              <Plus size={16} className="mr-1" /> {saving ? "Saving..." : "Log communication"}
            </Button>
          </div>
        </form>
      )}

      {items.length === 0 ? (
        <div className="mt-5 text-sm text-slate-500">No communications yet.</div>
      ) : (
        <ol className="mt-5 relative border-l border-slate-200 ml-3">
          {items.map((c, i) => {
            const Icon = c.kind === "call" ? PhoneCall : MessageCircle;
            return (
              <li key={c.id} className="ml-6 pb-5" data-testid={`comm-${i}`}>
                <span className="absolute -left-[7px] mt-1.5 h-3.5 w-3.5 rounded-full bg-white ring-4 ring-slate-100 flex items-center justify-center">
                  <Icon size={9} className={c.kind === "call" ? "text-sky-600" : "text-emerald-600"} />
                </span>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm text-slate-500 inline-flex items-center gap-1"><Clock size={12} /> {fmtDateTime(c.at)}</div>
                  {!c._legacy && !entry.is_complete && (
                    <button type="button" onClick={() => removeOne(c.id)}
                            className="text-xs text-slate-400 hover:text-red-600"
                            data-testid={`comm-remove-${i}`}>Remove</button>
                  )}
                </div>
                <div className="text-slate-900 font-medium mt-0.5">
                  {c.kind === "call" ? "Call" : `Message · ${c.channel ? c.channel : "—"}`}
                </div>
                {c.note && <div className="text-sm text-slate-700 mt-0.5 whitespace-pre-wrap">{c.note}</div>}
                <div className="text-xs text-slate-400 mt-0.5">by {c.by || "—"}{c._legacy ? " · legacy" : ""}</div>
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}

function PatientTimelineCard({ patientName, currentEntryId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/patients/history", { params: { name: patientName } });
        if (alive) setEvents((data.events || []).slice().reverse());
      } catch (e) { toast.error(formatApiError(e)); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [patientName]);

  if (loading) {
    return <Card className="p-6 border-slate-200 text-sm text-slate-500" data-testid="patient-timeline-loading">Loading patient timeline...</Card>;
  }

  return (
    <Card className="p-6 border-slate-200" data-testid="patient-timeline-card">
      <h2 className="font-display font-semibold text-slate-900 flex items-center gap-2">
        <Clock size={18} className="text-slate-700" /> Patient Timeline
      </h2>
      <p className="text-sm text-slate-500 mt-1">
        Every follow-up scheduled, communication logged and closure for {patientName}, newest first.
      </p>
      {events.length === 0 ? (
        <div className="mt-6 text-sm text-slate-500">No history yet.</div>
      ) : (
        <ol className="mt-5 relative border-l border-slate-200 ml-3">
          {events.map((e, i) => {
            const isCurrent = e.entry_id === currentEntryId;
            let icon = Clock, tone = "text-slate-500", title = "", body = e.note || "";
            if (e.type === "scheduled") { icon = CalendarClock; tone = "text-sky-600"; title = "Follow-up scheduled"; }
            else if (e.type === "communication") {
              icon = e.kind === "call" ? PhoneCall : MessageCircle;
              tone = e.kind === "call" ? "text-sky-600" : "text-emerald-600";
              title = e.kind === "call" ? "Call" : `Message · ${e.channel || "—"}`;
            }
            else if (e.type === "closed") { icon = XCircle; tone = "text-slate-700"; title = `Follow-up closed · ${e.reason || ""}`; }
            const Ic = icon;
            return (
              <li key={i} className={`ml-6 pb-5 ${isCurrent ? "" : ""}`} data-testid={`patient-timeline-${i}`}>
                <span className="absolute -left-[7px] mt-1.5 h-3.5 w-3.5 rounded-full bg-white ring-4 ring-slate-100 flex items-center justify-center">
                  <Ic size={9} className={tone} />
                </span>
                <div className="text-sm text-slate-500 inline-flex items-center gap-1">
                  <Clock size={12} /> {fmtDateTime(e.at)}
                  {isCurrent && <Badge className="ml-2 bg-sky-50 text-sky-700 border-sky-100 hover:bg-sky-50">this entry</Badge>}
                </div>
                <div className="text-slate-900 font-medium mt-0.5">{title}</div>
                {body && <div className="text-sm text-slate-700 mt-0.5 whitespace-pre-wrap">{body}</div>}
                {e.by && <div className="text-xs text-slate-400 mt-0.5">by {e.by}</div>}
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}


/* ----- review actions ----- */
function ReviewActions({ entry, onDone }) {
  const [busy, setBusy] = useState(false);
  const sent = async () => {
    setBusy(true);
    try { await api.post(`/entries/${entry.id}/review-sent`); toast.success(entry.review_stage >= 3 ? "3rd reminder logged — auto-completed" : "Reminder logged"); onDone(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const received = async () => {
    setBusy(true);
    try { await api.post(`/entries/${entry.id}/review-received`); toast.success("Review received — completed"); onDone(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <Card className="p-6 border-slate-200" data-testid="review-action-card">
      <h2 className="font-display font-semibold text-slate-900 flex items-center gap-2">
        <Star size={18} className="text-emerald-600" /> Review reminder — #{entry.review_stage} of 3
      </h2>
      <p className="text-sm text-slate-500 mt-1">
        Send a review request to the patient. Reminds again in 7 days if not received. After the 3rd, this entry auto-completes.
      </p>
      <div className="mt-5 flex flex-wrap gap-3">
        <Button onClick={received} disabled={busy}
                className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="review-received-button">
          <CheckCircle2 size={16} className="mr-1.5" /> Review received
        </Button>
        <Button onClick={sent} disabled={busy} variant="outline" data-testid="review-sent-button">
          Mark reminder #{entry.review_stage} sent
        </Button>
      </div>
    </Card>
  );
}

/* ----- lab actions ----- */
function LabActions({ entry, onDone }) {
  const [busy, setBusy] = useState(false);
  const [when, setWhen] = useState("");
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/entries/${entry.id}/lab-received`, { received_date: when || null }); toast.success("Lab work received"); onDone(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <Card className="p-6 border-slate-200" data-testid="lab-action-card">
      <h2 className="font-display font-semibold text-slate-900 flex items-center gap-2">
        <FlaskConical size={18} className="text-violet-600" /> Mark lab work received
      </h2>
      <div className="mt-4 grid grid-cols-2 gap-3 items-end">
        <div className="space-y-2">
          <Label>Received on (defaults to today)</Label>
          <Input type="date" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="lab-received-date-input" />
        </div>
        <Button onClick={submit} disabled={busy}
                className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="lab-received-submit">
          <CheckCircle2 size={16} className="mr-1.5" /> Mark received
        </Button>
      </div>
    </Card>
  );
}

function HistoryCard({ entry }) {
  const events = [];
  (entry.followup_history || []).forEach((h) => events.push({
    at: h.at, title: `Follow-up ${h.method === "whatsapp" ? "WhatsApp" : "Call"}`,
    body: `${h.outcome === "appointment_booked" ? "Appointment booked — completed" : "Rescheduled"}${h.note ? " · " + h.note : ""}`,
    by: h.by, icon: h.method === "whatsapp" ? MessageCircle : PhoneCall, tone: "text-sky-600",
  }));
  (entry.review_history || []).forEach((h) => events.push({
    at: h.at, title: h.action === "received" ? "Review received" : `Reminder #${h.stage} sent`,
    body: "", by: h.by, icon: Star, tone: "text-emerald-600",
  }));
  events.sort((a, b) => new Date(b.at) - new Date(a.at));

  if (events.length === 0) {
    return (
      <Card className="p-6 border-slate-200 text-sm text-slate-500" data-testid="history-card">
        No history yet. Actions you take will show up here.
      </Card>
    );
  }

  return (
    <Card className="p-6 border-slate-200" data-testid="history-card">
      <h2 className="font-display font-semibold text-slate-900">History</h2>
      <ol className="mt-5 relative border-l border-slate-200 ml-3">
        {events.map((e, i) => {
          const Icon = e.icon;
          return (
            <li key={i} className="ml-6 pb-5" data-testid={`history-${i}`}>
              <span className="absolute -left-[7px] mt-1.5 h-3.5 w-3.5 rounded-full bg-white ring-4 ring-slate-100 flex items-center justify-center">
                <Icon size={9} className={e.tone} />
              </span>
              <div className="text-sm text-slate-500">{fmtDateTime(e.at)}</div>
              <div className="text-slate-900 font-medium">{e.title}</div>
              {e.body && <div className="text-sm text-slate-600">{e.body}</div>}
              <div className="text-xs text-slate-400 mt-0.5">by {e.by}</div>
            </li>
          );
        })}
      </ol>
    </Card>
  );
}
